import { isNumber, isString, isUndefined } from 'lodash';
import { useRouter } from 'next/router';
import { memo, type ReactNode } from 'react';

import { cn } from '@air/utils-tailwind';

import { hasAnnotation } from '~/components/Annotations/shared/annotationUtils';
import { type ClipDiscussion } from '~/components/Annotations/shared/types';
import { ANNOTATION_COMMENT_ID } from '~/components/AnnotationsV2/AnnotationsAvatarsContainer/AnnotationAvatarContainer/components/AnnotationComment';
import { isViewingDiscussionWithId } from '~/store/router/selectors';
import { useAirSelector } from '~/utils/ReduxUtils';

export type TopCommentContainerProps = {
  children: ReactNode;
  discussion: ClipDiscussion;
  onClick: () => void;
};

export const TopCommentContainer = memo(({ discussion, children, onClick }: TopCommentContainerProps) => {
  const { query } = useRouter();
  const isDiscussionActive = useAirSelector((st) => isViewingDiscussionWithId(st, discussion.id));
  const timestamp = isString(query.ts) ? parseFloat(query.ts) : undefined;
  const isTimestampActive = isNumber(timestamp) && timestamp === discussion.timestamp;
  const isAnnotation = hasAnnotation(discussion);

  return isAnnotation || !isUndefined(discussion.timestamp) ? (
    // biome-ignore lint/a11y/useKeyWithClickEvents: We're using a div instead of a button to prevent invalid DOM nesting.
    // biome-ignore lint/a11y/useSemanticElements: We're using a div instead of a button to prevent invalid DOM nesting.
    <div
      className={cn(
        'cursor-pointer ads:hover:bg-hover-subtle hover:bg-gray-3',
        isDiscussionActive ? 'ads:bg-subtle bg-gray-3' : 'bg-transparent',
        isTimestampActive ? 'ads:bg-subtle bg-gray-3' : undefined,
        isDiscussionActive ? '[&>.comment-menu]:opacity-100' : undefined,
      )}
      /**
       * Use to prevent the mousedown event listener from firing when the user clicks on the comment.
       * This event listener is located in:
       * `/src/components/Annotations/shared/useClearAnnotationOnMouseDownOutside.ts`
       */
      id={isAnnotation ? ANNOTATION_COMMENT_ID : undefined}
      onClick={onClick}
      role="button"
      tabIndex={0}
    >
      {children}
    </div>
  ) : (
    // biome-ignore lint/complexity/noUselessFragments: We need to wrap the children in a Fragment to ensure that we're returning a JSX element.
    <>{children}</>
  );
});

TopCommentContainer.displayName = 'TopCommentContainer';
